/**
 * "Neste dia" no painel: o `on_this_day_card.dart`.
 *
 * A primeira fatia de conteúdo do painel de lá (`dashboard_screen.dart:392`),
 * e uma das poucas sem `so:` — não desliga nos ajustes. É uma fileira de
 * fichinhas de 150 com o que aconteceu num mesmo dia e mês de outros anos: uma
 * foto do álbum, um filme que entrou na lista, uma frase dita no Refúgio.
 *
 * Quando não há nada, a seção inteira some — o `SizedBox.shrink()` de lá. Um
 * cabeçalho "Neste dia" com o vão vazio embaixo seria pior do que não ter.
 *
 * As palavras miúdas ficam como estão lá, em inglês (`'N memories from the
 * past'`, `'3 years ago'`, `'... said'`): é o que o outro app mostra.
 *
 * O que o navegador obrigou a mudar:
 *
 * - **Cada ficha é o próprio link.** Lá o `onTap` de cada uma vai para um lugar
 *   diferente conforme a fonte — álbum, cinema ou Refúgio —, então a seção não
 *   pode ser um link só como as outras. A setinha do canto fica: lá ela também
 *   é enfeite, o `FeatureSection` do `OnThisDayCard` não recebe `onTap`.
 * - **Sem a entrada escalonada.** Lá cada ficha sobe 20 com um atraso de 100 em
 *   100 até 600. Aqui a fileira já chega inteira e um `prefers-reduced-motion`
 *   apagaria a animação de todo jeito; o que sobrou é o levantar do ponteiro.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  COLECAO_CINEMA,
  COLECOES,
  fotoDeMapa,
  lembrancasDeHoje,
  mensagemDeMapa,
  traduzir,
  usarColecao,
  type DocMidia,
  type FonteDaLembranca,
  type Lembranca,
  type NomeDoIcone,
} from '@nosso-sempre/shared';
import { Icone } from './Icone';
import { Secao, SetaDaSecao } from './Secao';
import './previas.css';

/** O `_sourceIcon`, `_sourceColor` e o destino do `onTap`, os três de uma vez. */
const FONTES: Record<FonteDaLembranca, { icone: NomeDoIcone; cor: string; para: string }> = {
  galeria: { icone: 'fotos', cor: 'var(--rosa-quartzo)', para: '/galeria' },
  cinema: { icone: 'cinema', cor: 'var(--rosa)', para: '/cinema' },
  refugio: { icone: 'balao', cor: 'var(--lavanda)', para: '/sanctuary' },
};

export function PreviaDeNesteDia() {
  const docsDeFotos = usarColecao(COLECOES.galeria);
  const docsDeMidia = usarColecao<DocMidia>(COLECAO_CINEMA);
  const docsDeFalas = usarColecao(COLECOES.santuario);

  const lembrancas = useMemo(
    () =>
      lembrancasDeHoje({
        fotos: docsDeFotos.map((d) => fotoDeMapa(d.id, d.dados)),
        midias: docsDeMidia,
        mensagens: docsDeFalas.map((d) => mensagemDeMapa(d.id, d.dados)),
      }),
    [docsDeFotos, docsDeMidia, docsDeFalas],
  );

  // O `SizedBox.shrink()` de lá: sem lembrança, sem seção.
  if (lembrancas.length === 0) return null;

  const quantas = lembrancas.length;

  return (
    // O respiro de cima vem daqui, e não de um `.bloco` no painel: só esta
    // prévia se apaga sozinha, e um `.bloco` vazio deixaria o vão dela.
    <div className="previa-nestedia-bloco">
      <Secao
        icone="historico"
        cor="var(--ouro)"
        titulo={traduzir('Neste dia')}
        legenda={traduzir('{quantas} {v} from the past', {
          quantas,
          v: quantas === 1 ? traduzir('memory') : traduzir('memories'),
        })}
        ponta={<SetaDaSecao />}
      >
        <ul className="previa-nestedia">
          {lembrancas.map((l) => (
            <li key={l.id}>
              <FichaDeLembranca lembranca={l} />
            </li>
          ))}
        </ul>
      </Secao>
    </div>
  );
}

/** O `_MemoryCard`: 150 de largura, a capa de 86 em cima e os dizeres embaixo. */
function FichaDeLembranca({ lembranca }: { lembranca: Lembranca }) {
  const { icone, cor, para } = FONTES[lembranca.fonte];
  const anos = lembranca.anos;

  return (
    <Link
      className="previa-nestedia-ficha"
      to={para}
      style={{ ['--cor-fonte' as string]: cor }}
      title={lembranca.legenda}
    >
      {lembranca.imagem ? (
        <span className="previa-nestedia-capa">
          <img src={lembranca.imagem} alt="" loading="lazy" />
          <span className="previa-nestedia-veu" aria-hidden="true" />
        </span>
      ) : (
        <span className="previa-nestedia-capa vazia" aria-hidden="true">
          <Icone nome={icone} tamanho={24} />
        </span>
      )}

      <span className="previa-nestedia-dizeres">
        <span className="previa-nestedia-nome">{lembranca.titulo}</span>
        <span className="previa-nestedia-quando">
          <Icone nome={icone} tamanho={10} />
          {traduzir('{anos} {v} ago', {
            anos,
            v: anos === 1 ? traduzir('year') : traduzir('years'),
          })}
        </span>
      </span>
    </Link>
  );
}
